import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { PricingHeader, Plan, Contract } from '../types';

interface ImportChangeSummary {
  total_unreviewed: number;
  by_entity_type: {
    controllers: number;
    supervisors: number;
  };
  by_change_type: {
    new: number;
    updated: number;
  };
  with_contracts: number;
  without_contracts: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState({
    pricingHeaders: 0,
    plans: 0,
    priceLists: 0,
    contracts: 0,
    activePlans: 0,
    activeContracts: 0
  });
  const [recentActivity, setRecentActivity] = useState<{
    pricingHeaders: PricingHeader[];
    plans: Plan[];
    contracts: Contract[];
  }>({
    pricingHeaders: [],
    plans: [],
    contracts: []
  });
  const [loading, setLoading] = useState(true);
  const [importChangeSummary, setImportChangeSummary] = useState<ImportChangeSummary | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch stats
        const [pricingHeadersRes, plansRes, priceListsRes, contractsRes] = await Promise.all([
          apiService.getPricingHeaders(0, 100),
          apiService.getPlans(1, 1),
          apiService.getPriceLists(1, 1),
          apiService.getContracts(1, 1)
        ]);

        // Fetch recent activity
        const [recentPricingHeaders, recentPlans, recentContracts] = await Promise.all([
          apiService.getPricingHeaders(0, 5),
          apiService.getPlans(1, 5),
          apiService.getContracts(1, 5)
        ]);
        
        // Fetch import changes summary
        try {
          const summaryRes = await apiService.get<ImportChangeSummary>('/api/v1/import-changes/stats/summary');
          setImportChangeSummary(summaryRes);
        } catch (error) {
          console.error('Error fetching import changes summary:', error);
        }

        setStats({
          pricingHeaders: pricingHeadersRes.length,
          plans: plansRes.total,
          priceLists: priceListsRes.total,
          contracts: contractsRes.total,
          activePlans: recentPlans.items.filter((p: Plan) => p.is_active).length,
          activeContracts: recentContracts.items.filter((c: Contract) => c.isactive).length
        });

        setRecentActivity({
          pricingHeaders: recentPricingHeaders,
          plans: recentPlans.items,
          contracts: recentContracts.items
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-number">{stats.pricingHeaders}</div>
          <div className="stat-label">Pricing Headers</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.plans}</div>
          <div className="stat-label">Total Plans</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.activePlans}</div>
          <div className="stat-label">Active Plans</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.priceLists}</div>
          <div className="stat-label">Price Lists</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.contracts}</div>
          <div className="stat-label">Total Contracts</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.activeContracts}</div>
          <div className="stat-label">Active Contracts</div>
        </div>
      </div>

      {/* Import Changes Alert */}
      {importChangeSummary && importChangeSummary.total_unreviewed > 0 && (
        <div className="card" style={{ marginBottom: '20px', backgroundColor: '#fff3cd', borderColor: '#ffeaa7' }}>
          <div className="card-header" style={{ backgroundColor: '#ffeaa7', borderColor: '#ffeaa7' }}>
            <h3 className="card-title">Import Changes Pending Review</h3>
            <Link to="/import-changes" className="btn btn-primary btn-sm">
              Review Changes
            </Link>
          </div>
          <div style={{ padding: '15px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
              <div>
                <strong>{importChangeSummary.total_unreviewed}</strong> Total Changes
              </div>
              <div>
                <strong>{importChangeSummary.by_change_type.new}</strong> New Entities
              </div>
              <div>
                <strong>{importChangeSummary.by_change_type.updated}</strong> Updates
              </div>
              <div>
                <strong>{importChangeSummary.with_contracts}</strong> With Contracts
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px' }}>
        {/* Recent Pricing Headers */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Pricing Headers</h3>
            <Link to="/pricing-headers" className="btn btn-outline btn-sm">
              View All
            </Link>
          </div>
          <div>
            {recentActivity.pricingHeaders.length === 0 ? (
              <p style={{ color: '#7f8c8d', textAlign: 'center', padding: '20px' }}>
                No pricing headers found
              </p>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.pricingHeaders.map((header) => (
                      <tr key={header.pricing_header_id}>
                        <td>{header.name}</td>
                        <td>{header.category || '-'}</td>
                        <td>
                          <span className={`badge ${header.is_active ? 'badge-success' : 'badge-danger'}`}>
                            {header.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Recent Plans */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Plans</h3>
            <Link to="/plans" className="btn btn-outline btn-sm">
              View All
            </Link>
          </div>
          <div>
            {recentActivity.plans.length === 0 ? (
              <p style={{ color: '#7f8c8d', textAlign: 'center', padding: '20px' }}>
                No plans found
              </p>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.plans.map((plan) => (
                      <tr key={plan.plan_id}>
                        <td>
                          <Link to={`/plans/${plan.plan_id}`} style={{ textDecoration: 'none', color: '#3498db' }}>
                            {plan.name}
                          </Link>
                        </td>
                        <td>
                          <span className={`badge ${plan.is_active ? 'badge-success' : 'badge-danger'}`}>
                            {plan.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(plan.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Recent Contracts */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Recent Contracts</h3>
            <Link to="/contracts" className="btn btn-outline btn-sm">
              View All
            </Link>
          </div>
          <div>
            {recentActivity.contracts.length === 0 ? (
              <p style={{ color: '#7f8c8d', textAlign: 'center', padding: '20px' }}>
                No contracts found
              </p>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Start Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActivity.contracts.map((contract) => (
                      <tr key={contract.contract_id}>
                        <td>
                          <Link to={`/contracts/${contract.contract_id}`} style={{ textDecoration: 'none', color: '#3498db' }}>
                            {contract.contract_name}
                          </Link>
                        </td>
                        <td>
                          <span className={`badge ${contract.isactive ? 'badge-success' : 'badge-danger'}`}>
                            {contract.isactive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{new Date(contract.start_date).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;